
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type ExpenseSubcategory = 
  | "purchases"
  | "salaries"
  | "rent"
  | "transport"
  | "marketing"
  | "subscriptions"
  | "loan_interest"
  | "bank_fees"
  | "penalties"
  | "bad_debts"
  | "income_tax"
  | "other_taxes";

interface ExpenseFormData {
  name: string;
  amount: number;
  notes: string;
  expenseType: "deducted" | "external";
  expenseCategory: string;
  expenseSubcategory: ExpenseSubcategory;
}

export const useExpenseSubmission = (onSuccess?: () => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const submitExpense = async (data: ExpenseFormData, validatedAmount: number | undefined) => {
    try {
      if (!data.name.trim()) {
        toast({
          title: "Erreur",
          description: "Le nom de la dépense est obligatoire",
          variant: "destructive",
        });
        return;
      }

      if (!data.expenseCategory) {
        toast({
          title: "Erreur",
          description: "La catégorie de dépense est obligatoire",
          variant: "destructive",
        });
        return;
      }

      if (data.expenseType === "deducted" && data.amount > (validatedAmount || 0)) {
        toast({
          title: "Erreur",
          description: "Le montant de la dépense ne peut pas être supérieur au chiffre d'affaires actuel",
          variant: "destructive",
        });
        return;
      }

      // Récupérer l'utilisateur courant
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erreur",
          description: "Vous devez être connecté pour ajouter une dépense",
          variant: "destructive",
        });
        return;
      }

      setIsLoading(true);

      const { error } = await supabase.from("poultry_transactions").insert({
        transaction_type: "expense",
        total_amount: data.amount,
        notes: `${data.name}${data.notes ? ` - ${data.notes}` : ''}`,
        transaction_date: new Date().toISOString(),
        quantity: 1,
        unit_price: data.amount,
        is_deducted_from_revenue: data.expenseType === "deducted",
        expense_category: data.expenseCategory,
        expense_subcategory: data.expenseSubcategory,
        created_by: user.id // Ajout de l'ID de l'utilisateur
      });

      if (error) throw error;

      toast({
        title: "Dépense ajoutée avec succès",
        description: `Montant: ${data.amount} FCFA`,
      });

      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      onSuccess?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout de la dépense:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter la dépense",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    submitExpense,
  };
};
